import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Stack,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActionArea,
  CardMedia,
  Avatar,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Skeleton,
  useTheme,
  useMediaQuery,
  IconButton,
  Fade,
  Badge,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import Pagination from '@mui/material/Pagination';

import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import { useTranslation } from 'react-i18next';
import { apiClient, brandsApi } from '../utils/apiClient';
import { extractPrimaryImage } from '../utils/imageHelpers';
import { useToast } from '../hooks/useToast';

const BASE_CATEGORIES = [
  'Real Estate',
  'Property',
  'Vehicles',
  'Electronics & Tech',
  'Home & Garden',
  'Entertainment',
  'Fashion & Beauty',
  'Jobs & Services',
  'Business',
];

const CATEGORY_ICONS = {
  'Real Estate': '🏗️',
  Property: '🏠',
  Vehicles: '🚗',
  'Electronics & Tech': '💻',
  'Home & Garden': '🏡',
  Entertainment: '🎮',
  'Fashion & Beauty': '👗',
  'Jobs & Services': '💼',
  Business: '🏢',
  ImmoNeuf: '🏗️',
  Immobilier: '🏠',
  Véhicules: '🚗',
  'Informatique et Multimédia': '💻',
  'Maison et Jardin': '🏡',
  'Loisirs et Divertissement': '🎮',
  'Habillement et Bien Être': '👗',
  'Emploi et Services': '💼',
  Entreprises: '🏢',
  Electronics: '📱',
  Fashion: '👕',
  Sports: '⚽',
  Books: '📚',
  Toys: '🧸',
  Jewelry: '💎',
  Furniture: '🪑',
};

const CATEGORY_KEY_MAP = {
  'Real Estate': 'realestate',
  Property: 'property',
  Vehicles: 'vehicles',
  'Electronics & Tech': 'electronicstech',
  'Home & Garden': 'homegarden',
  Entertainment: 'entertainment',
  'Fashion & Beauty': 'fashionbeauty',
  'Jobs & Services': 'jobsservices',
  Business: 'business',
};

const getCategoryKey = (category) => CATEGORY_KEY_MAP[category] || 'other';

const getCategoryIcon = (category) => CATEGORY_ICONS[category] || '📦';

const formatPrice = (price) => `${new Intl.NumberFormat('en-US').format(price ?? 0)} TND`;

const Ecommerce = () => {
  const { token } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = isDownSm;
  const { t } = useTranslation();
  const borderRadius = `${theme.shape.borderRadius}px`;
  const compactRadius = `${Math.max(theme.shape.borderRadius - 10, 12)}px`;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [brands, setBrands] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [reservations, setReservations] = useState({});
  const [reservationCaps, setReservationCaps] = useState({});
  const [userReservationFlags, setUserReservationFlags] = useState({});
  const [loadingReservations, setLoadingReservations] = useState({});
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationProduct, setReservationProduct] = useState(null);
  const [reservationResult, setReservationResult] = useState(null);

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await brandsApi.getAll();
        const brandsData = Array.isArray(response) ? response : (response?.data || []);
        setBrands(brandsData.filter(brand => brand.isActive));
      } catch (error) {
        console.error('Failed to fetch brands:', error);
      }
    };
    fetchBrands();
  }, []);

  const canReserve = useCallback((product) => product?.isReservable === true, []);

  const fetchProducts = useCallback(
    async (page = 1, brand = '') => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          page,
          limit: 12,
          includeOutOfStock: 'true',
          ...(brand && { brand }),
        }).toString();

        const data = await apiClient.get(`/api/products?${query}`);

        if (!data?.success || !data?.data?.products) {
          throw new Error(t('ecommerce.errors.invalidResponse', 'Invalid API response structure'));
        }

        setProducts(data.data.products);
        setTotalPages(data.data.pagination?.totalPages || 1);
        setCurrentPage(data.data.pagination?.currentPage || page);

        const reservationCandidates = data.data.products.filter(
          (p) => canReserve(p) || p.tags?.includes('auction')
        );
        const productIds = reservationCandidates.map((p) => p._id).filter(Boolean);

        if (token && productIds.length) {
          const results = await Promise.all(
            productIds.map(async (id) => {
              try {
                const rs = await apiClient.get(`/api/auction-reservations/status/${id}`);
                if (!rs.success) throw new Error('API success false');
                const d = rs.data || {};
                const active = d.activeReservations ?? 0;
                const total = d.totalReservations ?? active;
                const cap =
                  d.maxReservations ||
                  reservationCandidates.find((p) => p._id === id)?.reservationParticipantLimit ||
                  100;
                const displayCount = active === 0 && total > 0 ? total : active;
                return {
                  id,
                  count: Math.min(displayCount, cap),
                  cap,
                  has: !!d.userHasReservation,
                };
              } catch (error) {
                console.warn('Reservation fetch failed', id, error.message);
                return { id, count: 0, cap: 100, has: false };
              }
            })
          );

          setReservations((prev) => {
            const next = { ...prev };
            results.forEach((r) => {
              next[r.id] = r.count;
            });
            return next;
          });

          setReservationCaps((prev) => {
            const next = { ...prev };
            results.forEach((r) => {
              next[r.id] = r.cap;
            });
            return next;
          });

          setUserReservationFlags((prev) => {
            const next = { ...prev };
            results.forEach((r) => {
              if (r.has) {
                next[r.id] = true;
              }
            });
            return next;
          });
        }

        if (token) {
          try {
            const myResJson = await apiClient.get('/api/auction-reservations/my-reservations');
            if (myResJson.success && Array.isArray(myResJson.data)) {
              const reservedIds = myResJson.data
                .filter((r) => ['pending', 'confirmed'].includes(r.status))
                .map((r) => r.product?.id)
                .filter(Boolean);
              if (reservedIds.length) {
                setUserReservationFlags((prev) => {
                  const next = { ...prev };
                  reservedIds.forEach((pid) => {
                    next[pid] = true;
                  });
                  return next;
                });
                setReservations((prev) => {
                  const next = { ...prev };
                  reservedIds.forEach((pid) => {
                    if (next[pid] === undefined || next[pid] === 0) {
                      next[pid] = 1;
                    }
                  });
                  return next;
                });
              }
            }
          } catch (error) {
            console.warn('Failed to hydrate user reservation list', error.message);
          }
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
        setTotalPages(1);
        setCurrentPage(1);
        toast.error(t('ecommerce.errors.loadProducts', 'Failed to load products. Please try again later.'));
      } finally {
        setLoading(false);
      }
    },
    [token, canReserve, t]
  );

  useEffect(() => {
    fetchProducts(currentPage, selectedBrand);
  }, [fetchProducts, currentPage, selectedBrand]);

  const handleBrandFilter = (brandId) => {
    setSelectedBrand(brandId);
    setCurrentPage(1);
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleBuyNow = (productId) => {
    if (!token) {
      navigate('/auth');
      return;
    }
    navigate(`/payment/${productId}`);
  };

  const handleReserveForAuction = (productId) => {
    if (!token) {
      navigate('/auth');
      return;
    }
    const product = products.find((p) => p._id === productId);
    setReservationProduct(product || null);
    setReservationResult(null);
    setShowReservationModal(true);
  };

  const confirmReservation = async () => {
    if (!reservationProduct || loadingReservations[reservationProduct._id]) return;

    const productId = reservationProduct._id;
    setLoadingReservations((prev) => ({ ...prev, [productId]: true }));

    try {
      const data = await apiClient.post('/api/auction-reservations/reserve', { productId });

      if (data.success) {
        const cap =
          reservationCaps[productId] ||
          reservationProduct.reservationParticipantLimit ||
          data.data?.maxReservations ||
          100;

        setReservations((prev) => ({
          ...prev,
          [productId]: data.data.totalReservations,
        }));

        setReservationResult({
          success: true,
          status: data.data.isAuctionFull ? 'full' : 'pending',
          totalReservations: data.data.totalReservations,
          spotsLeft: Math.max(cap - data.data.totalReservations, 0),
          cap,
        });

        setUserReservationFlags((prev) => ({
          ...prev,
          [productId]: true,
        }));
      } else {
        setReservationResult({
          success: false,
          errorKey: data.message ? null : 'ecommerce.reservationResult.errorDefault',
          errorMessage: data.message || null,
        });

        if ((data.message || '').toLowerCase().includes('already have a reservation')) {
          setUserReservationFlags((prev) => ({ ...prev, [productId]: true }));
        }
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      setReservationResult({
        success: false,
        errorKey: 'ecommerce.reservationResult.errorCatch',
        errorMessage: null,
      });

      if ((error.message || '').toLowerCase().includes('already')) {
        setUserReservationFlags((prev) => ({ ...prev, [productId]: true }));
      }
    } finally {
      setLoadingReservations((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const getReservationPercentage = useCallback(
    (productId) => {
      const cap = reservationCaps[productId] || 100;
      const value = reservations[productId] || 0;
      if (cap === 0) return 0;
      return Math.min((value / cap) * 100, 100);
    },
    [reservationCaps, reservations]
  );

  const isFullyReserved = useCallback(
    (productId) => {
      const cap = reservationCaps[productId] || 100;
      return (reservations[productId] || 0) >= cap;
    },
    [reservationCaps, reservations]
  );

  const getAuctionStartTime = (product) => {
    if (product?.auctionStartTime) {
      return new Date(product.auctionStartTime);
    }
    if (!product?._id) return new Date();
    const daysToAdd = (Math.abs(product._id.toString().charCodeAt(0)) % 7) + 1;
    const startTime = new Date();
    startTime.setDate(startTime.getDate() + daysToAdd);
    return startTime;
  };

  const getStartingPrice = (product) =>
    product?.startingPrice ?? product?.minBid ?? (product?.price ? product.price * 0.6 : 0);

  const isScheduledForAuction = useCallback(
    (product) => {
      if (canReserve(product)) {
        const start = getAuctionStartTime(product);
        return start > new Date();
      }

      const isAuctionItem =
        product?.isAuctionItem ||
        product?.tags?.includes('auction') ||
        product?.category === 'Auction Items' ||
        product?.auctionScheduled === true;

      if (!isAuctionItem) {
        return false;
      }

      const start = getAuctionStartTime(product);
      return start > new Date();
    },
    [canReserve]
  );

  // Brand lookup so cards can resolve a brand that came back as a bare id
  const brandsById = useMemo(() => {
    const map = {};
    brands.forEach((brand) => {
      if (brand?._id) map[brand._id] = brand;
    });
    return map;
  }, [brands]);

  // The API populates `brand` on most paths, but falls back to a raw id on others.
  const getBrandName = useCallback(
    (product) => {
      const brand = product?.brand;
      if (!brand) return null;
      if (typeof brand === 'object') {
        return brand.name || brandsById[brand._id]?.name || null;
      }
      return brandsById[brand]?.name || null;
    },
    [brandsById]
  );

  // Filter products by brand on the frontend
  const filteredProducts = useMemo(() => {
    if (!selectedBrand) return products;
    return products.filter((p) => {
      if (!p.brand) return false;
      // If brand is an object with _id
      if (typeof p.brand === 'object' && p.brand !== null) {
        if (p.brand._id && typeof p.brand._id === 'string') return p.brand._id === selectedBrand;
        if (typeof p.brand.toString === 'function') return p.brand.toString() === selectedBrand;
      }
      // If brand is a string
      if (typeof p.brand === 'string') return p.brand === selectedBrand;
      // If brand is an ObjectId (non-string)
      if (typeof p.brand === 'object' && typeof p.brand.toHexString === 'function') return p.brand.toHexString() === selectedBrand;
      return false;
    });
  }, [products, selectedBrand]);

  const ProductCard = ({ product }) => {
    const productId = product._id || product.id || product.sku;
    const auctionEligible = isScheduledForAuction(product) || canReserve(product);
    const reservationFilled = isFullyReserved(productId);
    const percentage = getReservationPercentage(productId);
    const brandName = getBrandName(product);
    const primaryImage = extractPrimaryImage(product);
    const imageSrc = primaryImage.thumbnailUrl || primaryImage.url;

    const primaryChip = product.soldOut
      ? { label: t('ecommerce.product.soldOut', 'Sold out'), color: 'error' }
      : auctionEligible
      ? { label: t('ecommerce.product.auction', 'Auction'), color: 'secondary', icon: <GavelIcon sx={{ fontSize: 16 }} /> }
      : null;

    return (
      <Card
        elevation={0}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius,
          overflow: 'hidden',
          position: 'relative',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: theme.vitria?.input,
          backdropFilter: 'blur(20px)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {/* Image Area */}
        <Box sx={{ position: 'relative', pt: '75%', bgcolor: theme.vitria?.soft }}>
          {imageSrc ? (
            <Box
              component="img"
              src={imageSrc}
              alt={product.name}
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                cursor: 'pointer',
              }}
              onClick={() => navigate(`/ecommerce/product/${productId}`)}
            />
          ) : (
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{
                position: 'absolute',
                inset: 0,
                color: theme.palette.text.disabled,
                fontSize: 48,
              }}
            >
              {getCategoryIcon(product.category)}
            </Stack>
          )}
          
          {/* Badges Overlay */}
          <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 12, left: 12, right: 12, justifyContent: 'space-between' }}>
             {brandName ? (
               <Chip
                  label={brandName}
                  size="small"
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(4px)',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 24
                  }}
                />
             ) : (
               // Keeps the auction chip right-aligned when there is no brand to show
               <Box />
             )}
             {primaryChip && (
                <Chip
                  icon={primaryChip.icon}
                  label={primaryChip.label}
                  color={primaryChip.color}
                  size="small"
                  sx={{ fontWeight: 700, height: 24 }}
                />
             )}
          </Stack>
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Tooltip title={product.name || t('ecommerce.product.unnamed', 'Unnamed product')}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ 
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.3,
                  mb: 0.5,
                  fontSize: '1.1rem'
                }}
                onClick={() => navigate(`/ecommerce/product/${productId}`)}
              >
                {product.name || t('ecommerce.product.unnamed', 'Unnamed product')}
              </Typography>
            </Tooltip>
            <Typography variant="h6" fontWeight={800} color="primary.main">
              {formatPrice(product.price)}
            </Typography>
          </Box>

          {auctionEligible && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {t('ecommerce.product.reservationStatus', 'Reservations')}
                </Typography>
                <Typography variant="caption" fontWeight={700} color={reservationFilled ? 'error.main' : 'success.main'}>
                  {Math.round(percentage)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={percentage}
                color={reservationFilled ? 'error' : percentage > 80 ? 'warning' : 'secondary'}
                sx={{ height: 6, borderRadius: compactRadius }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                 {reservationFilled ? 'Full' : `${Math.round(percentage)}% filled`}
                 {product?.participationFee > 0 && ` • Fee: ${product.participationFee} TND`}
              </Typography>
            </Paper>
          )}
        </CardContent>

        <Divider sx={{ opacity: 0.5 }} />

        <Stack direction="row" spacing={1} sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            color="primary"
            startIcon={<ShoppingCartCheckoutIcon />}
            onClick={() => handleBuyNow(productId)}
            disabled={product.soldOut}
            sx={{ borderRadius, textTransform: 'none', fontWeight: 600 }}
          >
            {product.soldOut ? t('ecommerce.product.soldOut', 'Sold') : t('ecommerce.product.buyNow', 'Buy')}
          </Button>

          {canReserve(product) && (
             <Button
                fullWidth
                variant="outlined"
                color={reservationFilled ? 'error' : 'secondary'}
                startIcon={userReservationFlags[productId] ? <CheckCircleIcon /> : <GavelIcon />}
                onClick={() => handleReserveForAuction(productId)}
                disabled={
                  reservationFilled ||
                  loadingReservations[productId] ||
                  product.isReservable !== true ||
                  userReservationFlags[productId]
                }
                sx={{ borderRadius, textTransform: 'none', fontWeight: 600 }}
              >
                {userReservationFlags[productId] ? 'Reserved' : 'Reserve'}
              </Button>
          )}
        </Stack>
      </Card>
    );
  };

  return (
    <SciFiBackground>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          // `clip` contains horizontal overflow without creating a scroll
          // container, which `hidden` would do — that breaks the sticky brand bar.
          overflowX: 'clip',
        }}
      >
        <Navbar />
        
        {/* Hero / Header Section */}
        <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 6 }, pb: 2 }}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
              <Box>
                <Typography variant={isDownSm ? "h4" : "h3"} fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
                  {t('ecommerce.title', 'Marketplace')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 600 }}>
                  {t('ecommerce.subtitle', 'Discover curated launches and auction-ready inventory.')}
                </Typography>
              </Box>
              
              {/* Search Bar - Responsive */}
              <Paper
                elevation={0}
                sx={{
                  p: '2px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  width: { xs: '100%', md: 400 },
                  borderRadius,
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  bgcolor: theme.vitria?.input,
                }}
              >
                <IconButton sx={{ p: '10px' }} aria-label="search">
                  <SearchIcon />
                </IconButton>
                <TextField
                  fullWidth
                  placeholder={t('ecommerce.search.placeholder', 'Search products...')}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{ ml: 1, flex: 1 }}
                />
              </Paper>
            </Stack>
          </Stack>
        </Container>

        {/* Brand filter — kept a direct child of the page column rather than
            nested in the header, so it stays stuck for the whole scroll: a
            sticky element can only travel within its own parent's box. */}
        <Box
          sx={{
            position: 'sticky',
            // Navbar is an AppBar position="sticky" whose Toolbar is 72px tall
            top: 72,
            zIndex: theme.zIndex.appBar - 1,
            py: 1.5,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(8,7,19,.84)' : 'rgba(255,255,255,.86)',
            backdropFilter: 'blur(24px) saturate(145%)',
            WebkitBackdropFilter: 'blur(24px) saturate(145%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Container maxWidth="xl">
              {isMobile ? (
                // Mobile: Horizontal Scroll
                <Stack 
                  direction="row" 
                  spacing={2} 
                  sx={{ 
                    overflowX: 'auto', 
                    pb: 1,
                    '::-webkit-scrollbar': { display: 'none' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    mx: -2, // Negative margin to allow full bleed scrolling
                    px: 2   // Padding to align content
                  }}
                >
                  {/* All Brands Option */}
                  <Tooltip title={t('ecommerce.brands.all', 'All Brands')}>
                    <Badge
                      overlap="circular"
                      badgeContent={selectedBrand === '' ? <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} /> : null}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                      <Avatar
                        onClick={() => handleBrandFilter('')}
                        sx={{
                          width: 56,
                          height: 56,
                          cursor: 'pointer',
                          border: selectedBrand === '' ? '3px solid' : '2px solid',
                          borderColor: selectedBrand === '' ? 'primary.main' : theme.palette.divider,
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                          transition: 'all 0.3s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        <CategoryOutlinedIcon />
                      </Avatar>
                    </Badge>
                  </Tooltip>
                  
                  {brands.map((brand) => {
                    const isSelected = selectedBrand === brand._id;
                    return (
                      <Tooltip key={brand._id} title={brand.name}>
                        <Badge
                          overlap="circular"
                          badgeContent={isSelected ? <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} /> : null}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        >
                          <Avatar
                            src={brand.logo?.url}
                            alt={brand.name}
                            onClick={() => handleBrandFilter(brand._id)}
                            sx={{
                              width: 56,
                              height: 56,
                              cursor: 'pointer',
                              border: isSelected ? '3px solid' : '2px solid',
                              borderColor: isSelected ? 'primary.main' : theme.palette.divider,
                              bgcolor: theme.palette.background.paper,
                              transition: 'all 0.3s',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                borderColor: 'primary.main',
                              }
                            }}
                          >
                            {!brand.logo?.url && brand.name.charAt(0).toUpperCase()}
                          </Avatar>
                        </Badge>
                      </Tooltip>
                    );
                  })}
                </Stack>
              ) : (
                // Desktop: Horizontal Filter Bar
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                   <Button
                      startIcon={<FilterListIcon />}
                      variant="text"
                      color="inherit"
                      sx={{ mr: 2, pointerEvents: 'none', fontWeight: 700 }}
                   >
                      {t('ecommerce.brands.filter', 'Brands')}:
                   </Button>
                   
                   {/* All Brands Option */}
                   <Tooltip title={t('ecommerce.brands.all', 'All Brands')}>
                     <Badge
                       overlap="circular"
                       badgeContent={selectedBrand === '' ? <CheckCircleIcon sx={{ fontSize: 14, color: 'primary.main' }} /> : null}
                       anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                     >
                       <Avatar
                         onClick={() => handleBrandFilter('')}
                         sx={{
                           width: 48,
                           height: 48,
                           cursor: 'pointer',
                           border: selectedBrand === '' ? '3px solid' : '2px solid',
                           borderColor: selectedBrand === '' ? 'primary.main' : theme.palette.divider,
                           bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                           transition: 'all 0.3s',
                           '&:hover': {
                             transform: 'scale(1.1)',
                             borderColor: 'primary.main',
                             boxShadow: theme.shadows[4],
                           }
                         }}
                       >
                         <CategoryOutlinedIcon />
                       </Avatar>
                     </Badge>
                   </Tooltip>
                   
                   {brands.map((brand) => {
                     const isSelected = selectedBrand === brand._id;
                     return (
                       <Tooltip key={brand._id} title={brand.name}>
                         <Badge
                           overlap="circular"
                           badgeContent={isSelected ? <CheckCircleIcon sx={{ fontSize: 14, color: 'primary.main' }} /> : null}
                           anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                         >
                           <Avatar
                             src={brand.logo?.url}
                             alt={brand.name}
                             onClick={() => handleBrandFilter(brand._id)}
                             sx={{
                               width: 48,
                               height: 48,
                               cursor: 'pointer',
                               border: isSelected ? '3px solid' : '2px solid',
                               borderColor: isSelected ? 'primary.main' : theme.palette.divider,
                               bgcolor: theme.palette.background.paper,
                               transition: 'all 0.3s',
                               '&:hover': {
                                 transform: 'scale(1.1)',
                                 borderColor: 'primary.main',
                                 boxShadow: theme.shadows[4],
                               }
                             }}
                           >
                             {!brand.logo?.url && brand.name.charAt(0).toUpperCase()}
                           </Avatar>
                         </Badge>
                       </Tooltip>
                     );
                   })}
                </Stack>
              )}
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="xl" sx={{ flex: 1, pt: 3, pb: 8 }}>
          {loading ? (
            <Grid container spacing={3}>
            </Grid>
          ) : filteredProducts.length ? (
            <>
              <Grid container spacing={3}>
                {filteredProducts.map((product) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product._id || product.id || product.sku}>
                    <ProductCard product={product} />
                  </Grid>
                ))}
              </Grid>
              {totalPages > 1 && (
                <Stack alignItems="center" sx={{ mt: 6 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isDownSm ? "medium" : "large"}
                    shape="rounded"
                  />
                </Stack>
              )}
            </>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 10,
                px: 2,
                borderRadius,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
              }}
            >
              <BoltOutlinedIcon color="disabled" sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom color="text.secondary">
                  {t('ecommerce.empty.title', 'No products found')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                  {t(
                    'ecommerce.empty.subtitle',
                    'Try adjusting the filters or check back soon for fresh inventory drops.'
                  )}
              </Typography>
            </Box>
          )}
        </Container>

        {/* Reservation Modal */}
        <Dialog
          open={showReservationModal && !!reservationProduct}
          onClose={() => {
            setShowReservationModal(false);
            setReservationProduct(null);
            setReservationResult(null);
          }}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: { borderRadius }
          }}
        >
          <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
            {t('ecommerce.dialog.title', 'Reserve Auction Spot')}
            <IconButton
              onClick={() => {
                setShowReservationModal(false);
                setReservationProduct(null);
                setReservationResult(null);
              }}
              sx={{ position: 'absolute', right: 12, top: 12 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {reservationProduct && !reservationResult && (
              <Stack spacing={3} sx={{ py: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                   <Avatar 
                     variant="rounded" 
                     src={reservationProduct.images?.[0]?.thumbnailUrl} 
                     sx={{ width: 64, height: 64, borderRadius }}
                   >
                      {getCategoryIcon(reservationProduct.category)}
                   </Avatar>
                   <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {reservationProduct.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('ecommerce.product.startingBid', {
                          amount: formatPrice(getStartingPrice(reservationProduct)),
                          defaultValue: `Starting bid: ${formatPrice(getStartingPrice(reservationProduct))}`,
                        })}
                      </Typography>
                   </Box>
                </Stack>
                
                <Paper variant="outlined" sx={{ p: 2, borderRadius, bgcolor: theme.palette.action.hover, border: 'none' }}>
                   <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        {t('ecommerce.product.participationFeeLabel', 'Participation Fee')}
                      </Typography>
                      <Chip 
                        label={t('ecommerce.product.participationFeeTokens', {
                          count: reservationProduct.participationFee || 0,
                          defaultValue: `${reservationProduct.participationFee || 0} TND`,
                        })} 
                        color="secondary" 
                        size="small" 
                        sx={{ fontWeight: 700 }}
                      />
                   </Stack>
                </Paper>

                <Alert severity="info" icon={<GavelIcon fontSize="small" />} sx={{ borderRadius }}>
                  {t(
                    'ecommerce.dialog.infoMessage',
                    'Secure your place before the room fills up. Payment is only required when the auction reaches full capacity.'
                  )}
                </Alert>
              </Stack>
            )}

            {reservationResult && (
              <Stack spacing={3} sx={{ py: 2, alignItems: 'center', textAlign: 'center' }}>
                {reservationResult.success ? (
                   <CheckCircleIcon color="success" sx={{ fontSize: 64 }} />
                ) : (
                   <ErrorOutlineIcon color="error" sx={{ fontSize: 64 }} />
                )}
                
                <Box>
                   <Typography variant="h6" fontWeight={700} gutterBottom>
                      {reservationResult.success ? 'Success!' : 'Error'}
                   </Typography>
                   <Typography color="text.secondary">
                      {reservationResult.success
                        ? reservationResult.status === 'full'
                          ? t('ecommerce.reservationResult.full', 'Auction confirmed! You secured the final spot.')
                          : t(
                              'ecommerce.reservationResult.success',
                              'Reservation successful. We will notify you as the auction fills.'
                            )
                        : reservationResult.errorMessage ||
                          (reservationResult.errorKey
                            ? t(reservationResult.errorKey)
                            : t('ecommerce.reservationResult.errorDefault', 'Failed to create reservation.'))}
                   </Typography>
                </Box>

                {reservationResult.success && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius, width: '100%', bgcolor: theme.palette.background.default }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('ecommerce.dialog.spotsRemaining', 'Spots Remaining')}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="primary">
                        {reservationResult.spotsLeft} / {reservationResult.cap || 100}
                      </Typography>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2.5 }}>
            {!reservationResult ? (
              <>
                <Button
                  onClick={() => {
                    setShowReservationModal(false);
                    setReservationProduct(null);
                    setReservationResult(null);
                  }}
                  color="inherit"
                  sx={{ borderRadius }}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={confirmReservation}
                  variant="contained"
                  color="secondary"
                  startIcon={<GavelIcon />}
                  disabled={
                    !reservationProduct ||
                    loadingReservations[reservationProduct._id] ||
                    userReservationFlags[reservationProduct._id]
                  }
                  sx={{ borderRadius, px: 3 }}
                >
                  {reservationProduct && loadingReservations[reservationProduct._id]
                    ? t('ecommerce.product.reserving', 'Reserving...')
                    : t('ecommerce.dialog.confirm', 'Confirm Reservation')}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  setShowReservationModal(false);
                  setReservationProduct(null);
                  setReservationResult(null);
                }}
                variant="contained"
                color={reservationResult.success ? 'success' : 'primary'}
                fullWidth
                sx={{ borderRadius }}
              >
                {t('common.close', 'Close')}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </SciFiBackground>
  );
};

export default Ecommerce;
